<template lang="html">
  <v-autocomplete
    ref="select"
    max-height="300"
    style="min-width:200px"
    clearable
    :label="label"
    :disabled="disabled"
    :loading="loading"
    :items="filteredItems"
    :value="selectedObject"
    item-text="nameFull"
    :placeholder="placeholder"
    :hint="hint"
    :persistent-hint="persistentHint"
    item-value="id"
    :no-data-text="noDataText"
    :error-messages="errorMessages"
    :filter="itemFilter"
    :search-input.sync="searchInput"
    :auto-select-first="true"
    :return-object="true"
    v-bind="$attrs"
    @change="selectionChange"
    @focus="gotFocus"
    @blur="gotBlur"
    @click:clear="clearSelection"
  >
    <template v-slot:prepend-item>
      <member-select-filter
        v-model="filterSelections"
        :filters="filters"
        class="ft-member-select-filter"
        @change="filterChanged()"
      />
    </template>
    <template
      v-slot:item="data"
    >
      <v-list-item-avatar :class="{'noSelect': data.item.noSelect}">
        <img :src="getPhoto(data.item)">
      </v-list-item-avatar>
      <v-list-item-content :class="'ftms_' + data.item.id">
        <v-list-item-title>{{ data.item.nameFull }}</v-list-item-title>
        <v-list-item-subtitle>
          <span
            style="display: inline-block"
            class="mr-2"
          >{{ showUsername ? data.item.username : data.item.memNum }}</span><span
            v-if="data.item.mType"
            class="caption"
          >({{ data.item.mType }} / {{ data.item.mShip }})</span>
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn
          v-if="enableInfoSelect"
          icon
          ripple
          @click.stop="infoSelect(data.item)"
        >
          <v-icon :color="(data.item.diningSpecialRequests ? 'warning' : 'grey lighten-1')">
            mdi-information
          </v-icon>
        </v-btn>
      </v-list-item-action>
    </template>
    <template slot="no-data">
      <div>
        <v-list>
          <v-list-item disabled>
            <v-list-item-content>
              <v-list-item-title>{{ noDataText }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
import {setItems, escapeRegExp, getMemberPhoto} from '../util/helpers'
import MemberSelectFilter from './MemberSelectFilter'

export default {
  name: 'MemberSelect',
  components: {MemberSelectFilter},
  props: {
    persistentHint: Boolean,
    placeholder: String,
    enableInfoSelect: Boolean,
    hint: String,
    label: String,
    value: Number,
    returnObject: Boolean,
    disabled: Boolean,
    errorMessages: Array,
    errorHandler: {
      type: Function,
      default () {}
    },
    filters: {
      type: Object,
      default () {
        return {
          mType: [],
          mShip: [],
          group: [],
          name: null
        }
      }
    },
    usedMemberIds: {
      type: Array,
      default () {
        return []
      }
    },
    filterSelections: {
      type: Object,
      default () {
        return {
          mType: null,
          mShip: null,
          group: null
        }
      }
    },
    preload: Object
  },
  data: function () {
    return {
      loading: false,
      items: [],
      selectedMember: null,
      searchInput: null,
      selectedObject: null,
      disableQuery: false,
      showUsername: false,
      lastSearch: null,
      apiCounter: 0,
      queryTimeout: null,
      blurTimeout: null,
      scrollTimeout: null,
      noOptionBlurCallback: null,
      scrollParents: []
    }
  },
  computed: {
    filteredItems () {
      return this.items.filter(m => {m.disabled = !!this.usedMemberMap[m.id] && m.id !== this.value; return true})
    },
    availableItems () {
      return this.items.filter(m => !this.usedMemberMap[m.id] || m.id === this.value)
    },
    usedMemberMap () {
      return this.usedMemberIds.reduce((acc, i) => {
        acc[i] = true
        return acc
      }, {})
    },
    itemMap () {
      return this.items.reduce((acc, m) => {
        acc[m.id] = m
        return acc
      }, {})
    },
    vSelect () {
      return this.$refs.select
    },
    noDataText () {
      if (this.loading) {
        return 'Working on it...'
      } else if (!this.lastSearch && !this.hasSelectFilter) {
        return 'Enter member\'s name or number to begin search'
      } else {
        if (this.hasSelectFilter) {
          if (this.lastSearch) {
            return 'No member found with selected filters and  name or number of "' + this.lastSearch + '"'
          } else {
            return 'No member found with selected filters.'
          }
        } else {
          return 'No member found with name or number of "' + this.lastSearch + '"'
        }
      }
    },
    hasFilters () {
      const filters = this.filters
      return !!filters.mType.length || !!filters.group.length || !!filters.mShip.length
    },
    hasSelectFilter () {
      return this.filterSelections.mType || this.filterSelections.mShip || this.filterSelections.group
    },
    indexOffset () {
      return this.hasFilters ? 2 : 0
    }
  },
  watch: {
    searchInput (search) {
      if (!search) return
      if (this.disableQuery) {
        console.log('Query disabled.  Skipping.')
        return
      }
      console.log('Running query', search)
      // Debounce the input and wait for a pause of at
      // least 200 milliseconds.
      clearTimeout(this.queryTimeout)
      this.queryTimeout = setTimeout(() => this.queryMembers(search), 200)
    }
  },
  mounted () {
    this.showUsername = this.$ftAC.usernameInSelector
    const preload = this.preload
    this.disableQuery = true
    if (preload && preload.id) {
      // Preload item
      console.log('Preloading member', preload)
      preload.isPreloaded = true
      this.items = [preload]
      this.selectedObject = preload
      this.selectedMember = preload
    }
    setTimeout(() => {this.disableQuery = false}, 100)
  },
  methods: {
    windowScrollHandler () {
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = setTimeout(() => this.updatePosition(), 100)
    },
    updatePosition () {
      console.log('Update position', this.vSelect)
      this.vSelect.updateMenuDimensions()
    },
    clearSelection () {
      console.log('Clearing selection')
      this.selectedMember = null
      this.vSelect.setValue(undefined)
      console.log('emitting clear: ', undefined)
      this.$emit('change', undefined)
      this.$emit('input', undefined)
      this.queryMembers(null)
    },
    itemFilter (item, queryText, itemText) {
      return true // Just return everything returned from the server
    },
    infoSelect (member) {
      this.blur()
      this.$emit('info-select', member)
    },
    blur () {
      this.$nextTick(() => {this.vSelect.blur()})
    },
    setFilter (filter, item, noOptionBlurCallback) {
      // Find where the previously selected item was
      this.noOptionBlurCallback = noOptionBlurCallback
      const selected = filter.result.indexOf(item)
      this.disableQuery = true
      setItems(this.filterSelections, filter)
      this.vSelect.lastItem = selected + 20 // render enough options to be able to scroll to where we need
      if (this.items !== filter.result) {
        this.items = filter.result
      }
      setTimeout(() => {
        // Focus on input after items change has had time
        this.vSelect.$refs.input.focus()
        // Then activate the menu
        this.$refs.select.activateMenu()
        setTimeout(() => {
          // Get menu object after it's had time to render (can't use nextTick here)
          const menu = this.vSelect.$refs.menu
          console.log(this.vSelect)
          const filteredItems = this.vSelect.filteredItems
          var index = filteredItems.indexOf(item)
          menu.getTiles()
          menu.listIndex = index
          menu.activate()
          setTimeout(() => {
            // Set next tile after menu has had time to activate (can't use nextTick here)
            if (!this.availableItems.length) return
            menu.nextTile()
            while (this.usedMemberMap[this.items[menu.listIndex].id] && menu.listIndex !== index) {
              menu.nextTile()
            }
          }, 100)
          this.disableQuery = false
        }, 0)
      }, 0)
    },
    selectionChange (selected) {
      console.log('Selection change', selected, this.$refs.select, this)
      var selectedId = selected ? selected.id : null
      if (this.usedMemberMap[selectedId]) {
        console.log("Can't select member in use")
        this.revertSelection()
        return
      }
      if (!selected) {
        this.revertSelection()
        return
      }
      const idChanged = (this.selectedMember ? this.selectedMember.id : null) !== selectedId
      if (idChanged && !this.usedMemberMap[selectedId]) {
        this.noOptionBlurCallback = null
        this.filterSelections.result = this.items
        this.$emit('filter-change', this.filterSelections)
        this.selectedMember = selected
        if (this.returnObject) {
          console.log('emitting object: ', selected)
          this.$emit('change', selected)
          this.$emit('input', selected)
        } else {
          console.log('emitting id: ', selectedId)
          this.$emit('change', selectedId)
          this.$emit('input', selectedId)
        }
      } else if (selectedId) {
        this.selectedMember = selected
      }
    },
    currentSelection () {
      return this.selectedMember
    },
    revertSelection () {
      console.log('Reverting previous selection', this.selectedMember, this.selectedObject)
      this.noOptionBlurCallback = null
      const selectedId = this.selectedMember && this.selectedMember.id
      if (selectedId && !this.itemMap[selectedId]) {
        // Original selection doesn't exist. Add previous
        console.log('Adding missing item')
        this.items.push(this.selectedMember)
      }
      this.selectedObject = this.selectedMember ? this.selectedMember : undefined
      if (this.vSelect) {
        console.log('Selecting item', this.selectedMember)
        this.vSelect.setValue(this.selectedMember ? this.selectedMember : undefined)
      }
    },
    gotFocus () {
      clearTimeout(this.blurTimeout)
      // if (this.value && (!this.selectedMember || this.selectedMember.isPreloaded)) {
      //   console.log('Filling member data for:', this.value)
      //   this.queryMembers(null, this.value, true)
      // }
    },
    gotBlur () {
      const vSelect = this.vSelect || {}
      this.blurTimeout = setTimeout(() => {
        if (!vSelect.isMenuActive) {
          const selectedId = this.selectedMember && this.selectedMember.id
          if (selectedId && (!this.itemMap[selectedId] || !this.selectedObject)) {
            this.revertSelection()
            return
          }
          if (!this.selectedMember && typeof this.noOptionBlurCallback === 'function' && !this.availableItems.length) {
            this.noOptionBlurCallback()
            this.noOptionBlurCallback = null
          }
        }
      }, 100)
    },
    setItemData (items) {
      this.vSelect.lastItem = 20 // render 20 options at a time
      this.items = items
      const cid = this.selectedMember && this.selectedMember.id
      const currentItem = cid && items.find(m => m.id === cid)
      if (currentItem && this.selectedMember.isPreloaded) {
        this.selectedMember = currentItem
      }
      this.windowScrollHandler()
    },
    getPhoto (item) {
      return getMemberPhoto(item.activePhoto, item.gender)
    },
    setIsLoading (val) {
      this.loading = val
    },
    clearList (clearAll) {
      console.log('Resetting list')
      const selected = []
      if (!clearAll) {
        for (let item of this.items) {
          if (item.id && item.id === this.value) {
            selected.push(item)
            break
          }
        }
      }
      this.setItemData(selected)
      return
    },
    filterChanged () {
      console.log('Filter selection changed', this.vSelect)
      this.filterSelections.result = this.items
      this.$emit('filter-change', this.filterSelections)
      setTimeout(() => {
        const select = this.vSelect
        const input = select.$refs.input
        select.focus()
        input.focus()
        const length = input.value.length
        this.$refs.select.activateMenu() // Show the menu
        setTimeout(() => {
          select.focus()
          input.focus()
          input.setSelectionRange(length, length) // Move cursor to end
        }, 100)
      }, 0)
      this.queryMembers(this.searchInput)
    },
    queryMembers (v, id) {
      console.log('queryMembers', v, id)
      if (this.selectedMember && this.selectedMember.id && this.selectedMember.nameFull === v) {
        return
      }
      this.lastSearch = v
      this.apiCounter++
      const myCount = this.apiCounter
      console.log('Starting search', myCount, v, id, this)
      var filter = []
      var methods = {}
      if (!id) {
        if (this.disableQuery) {
          console.log('Query disabled')
          return
        }
        // Check if the search value is reasonable
        if (!this.hasSelectFilter && (!v || !v.trim().replace(/[^\dA-zÀ-ÿ*_ ]/, ''))) {
          console.log('Empty/invalid search 1', v)
          this.clearList()
          return
        }
        // this.filterSelections.name = v // Store for later use by caller
        if (v === null || v === undefined) {
          v = ''
        }
        // This name parsing probably needs some work.
        const cleaned = v.trim().replace(/,/, ', ')
        const trimPrefix = cleaned.match(/[^ ]*\.[$ ]/) !== null
        // const lastFirst = cleaned.match(/[^ ]*,[$ ]/) !== null
        const searchParts = cleaned.replace(/[^\dA-zÀ-ÿ'_ ]/, '').replace(/^'|'$/, '').replace(/ +/, ' ').replace(/_/, '\\_').split(' ')
        if (trimPrefix) searchParts.shift() // Remove mr. sr., etc. from start
        if (!searchParts.length) {
          console.log('Empty/invalid search 2', v)
          this.clearList()
          return
        }


        methods.filterSearchWords = [v]

        if (this.hasSelectFilter) {
          if (this.filterSelections.group) methods.filterMemberGroup = [this.filterSelections.group]
          if (this.filterSelections.mType) methods.filterMType = [this.filterSelections.mType]
          if (this.filterSelections.mShip) methods.filterMShip = [this.filterSelections.mShip]
        }
      } else {
        // Searching by member's record id (not member number)
        // Used by preloader, to make sure we have full up-to-date data for a pre-selected member
        methods.filterById = [id]
      }

      this.setIsLoading(true)

      this.$ftAPI().get(
        [{
          command: 'memberSearch',
          load: (d) => {
            // Make sure we're still in the same search
            if (myCount < this.apiCounter) {
              console.log('Skip search result, newer exists', myCount, filter, methods)
              return
            }
            this.setIsLoading(false)
            if (!id && v && !v.trim().match(/\s|,|\./i)) {
              // Could be matching member number
              const memNumSearch = new RegExp('^' + escapeRegExp(v.trim()) + '$', 'i')
              const memberNumMatch = []
              const nonMemNumMatch = []
              for (let m of d) {
                if (m.memNum && m.memNum.match(memNumSearch)) {
                  memberNumMatch.push(m)
                } else {
                  nonMemNumMatch.push(m)
                }
              }
              d = memberNumMatch.concat(nonMemNumMatch)
            }
            this.setItemData(d)
          },
          error: (e) => {
            if (myCount < this.apiCounter) {
              console.log('Skip error result, newer exists', myCount, filter, methods)
              return
            }
            this.setIsLoading(false)
            if (!id) {
              this.errorHandler(e)
            }
          },
          params: {
            filter: filter,
            filter_methods: methods,
            include_disabled: false
          }
        }]
      )
      console.log('Did search?', myCount, filter)
    }
  }
}
</script>
<style>
  .ft-memberSelect {
    max-width: 320px;
  }
</style>
